import { useEffect, useRef } from 'react';

/**
 * The route to you, drawn as rings.
 *
 * Your device sits at the centre. Each ring is one hop the data takes —
 * router, provider, backbone, server — and the points on a ring are the
 * machines at that hop. Traffic starts at the outer ring and works inward,
 * hop by hop, converging on you. Static drifts between the outer rings and
 * dulls whatever passes through it; the innermost ring is the shield, and
 * anything dulled is cleared as it crosses. One continuous scene.
 */
const RINGS = [
  { label: 'Router',   r: 0.30, nodes: 6,  speed:  0.010 },
  { label: 'Provider', r: 0.50, nodes: 12, speed: -0.006 },
  { label: 'Backbone', r: 0.70, nodes: 20, speed:  0.004 },
  { label: 'Server',   r: 0.92, nodes: 30, speed: -0.0025 },
];
const SHIELD_R = 0.16;
const STATIC = [
  { r: 0.60, a: 0.4, w: 0.9, speed: 0.05 },
  { r: 0.81, a: 2.9, w: 1.1, speed: -0.035 },
  { r: 0.60, a: 4.4, w: 0.7, speed: 0.04 },
];

export default function RingScene({ height = 560, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, R = 0, cx = 0, cy = 0, raf, last = performance.now();

    let theme = readTheme();
    const obs = new MutationObserver(() => { theme = readTheme(); });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    // ---- static layout: jittered angles per node, per ring
    const rings = RINGS.map((ring, k) => ({
      ...ring, rot: 0,
      angles: Array.from({ length: ring.nodes }, (_, i) => (i / ring.nodes) * Math.PI * 2 + hash(k * 97 + i) * 0.25),
    }));

    const s = { t: 0, packets: [], sparks: [], pulses: [], shieldPulse: 0, pointer: null, hot: null, spawnAcc: 0 };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = rect.width; H = rect.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2; cy = H / 2;
      R = Math.min(W, H) / 2 - 28;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const onMove = (e) => { const b = canvas.getBoundingClientRect(); s.pointer = { x: e.clientX - b.left, y: e.clientY - b.top }; };
    const onLeave = () => { s.pointer = null; s.hot = null; };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);

    // ---- helpers
    const nodePos = (k, i) => {
      const ring = rings[k];
      const a = ring.angles[i] + ring.rot;
      return { x: cx + Math.cos(a) * ring.r * R, y: cy + Math.sin(a) * ring.r * R, a };
    };
    const nearestInner = (k, i) => {
      // The node on the next ring in whose direction this one lies.
      if (k === 0) return null;
      const a = rings[k].angles[i] + rings[k].rot;
      let best = 0, bd = 9;
      rings[k - 1].angles.forEach((b, j) => {
        const d = Math.abs(angDiff(a, b + rings[k - 1].rot));
        if (d < bd) { bd = d; best = j; }
      });
      return best;
    };
    const staticPos = (c) => {
      const a = c.a + s.t * c.speed;
      return { x: cx + Math.cos(a) * c.r * R, y: cy + Math.sin(a) * c.r * R, rad: c.w * R * 0.14 };
    };

    const spawn = () => {
      const i = Math.floor(Math.random() * rings[3].nodes);
      s.packets.push({ k: 3, i, to: nearestInner(3, i), t: 0, speed: 0.55 + Math.random() * 0.35, taint: 0, clean: 0, trail: [] });
    };

    // ---- simulation
    const step = (dt) => {
      s.t += dt;
      rings.forEach((r) => { r.rot += r.speed * dt; });
      s.shieldPulse = Math.max(0, s.shieldPulse - dt * 1.4);
      s.spawnAcc += dt;
      while (s.spawnAcc > 0.28) { s.spawnAcc -= 0.28; if (s.packets.length < 60) spawn(); }

      for (const p of s.packets) {
        p.t += p.speed * dt;
        // position along the current leg (ring k node i → ring k-1 node `to`, or centre)
        const from = nodePos(p.k, p.i);
        const to = p.k === 0 ? { x: cx, y: cy } : nodePos(p.k - 1, p.to);
        const e = easeInOut(Math.min(1, p.t));
        const bow = bowPoint(from, to, cx, cy);
        p.x = quad(from.x, bow.x, to.x, e); p.y = quad(from.y, bow.y, to.y, e);
        p.trail.push({ x: p.x, y: p.y }); if (p.trail.length > 10) p.trail.shift();
        p.clean = Math.max(0, p.clean - dt * 0.9);

        // static dulls what passes through it
        for (const c of STATIC) {
          const sp = staticPos(c);
          const d = Math.hypot(p.x - sp.x, p.y - sp.y);
          if (d < sp.rad * 1.6) {
            p.taint = Math.min(1, p.taint + dt * 1.3);
            if (Math.random() < 0.12) s.sparks.push(spark(p.x, p.y, 'static'));
          }
        }

        // the shield clears it on the way in
        const dc = Math.hypot(p.x - cx, p.y - cy);
        if (p.k === 0 && dc < SHIELD_R * R && p.taint > 0.05) {
          for (let n = 0; n < 8; n++) s.sparks.push(spark(p.x, p.y, 'clean'));
          p.taint = 0; p.clean = 1; s.shieldPulse = 1;
        }

        if (p.t >= 1) {
          if (p.k === 0) { p.done = true; s.pulses.push({ life: 1 }); }
          else { p.k -= 1; p.i = p.to; p.to = nearestInner(p.k, p.i); p.t = 0; p.speed = 0.55 + Math.random() * 0.35; }
        }
      }
      s.packets = s.packets.filter((p) => !p.done);
      for (const k of s.sparks) { k.x += k.vx * dt; k.y += k.vy * dt; k.life -= dt * 1.8; }
      s.sparks = s.sparks.filter((k) => k.life > 0);
      for (const p of s.pulses) p.life -= dt * 0.7;
      s.pulses = s.pulses.filter((p) => p.life > 0);

      // hover: which node is under the pointer, and its route home
      s.hot = null;
      if (s.pointer) {
        let bd = 18;
        rings.forEach((ring, k) => ring.angles.forEach((_, i) => {
          const n = nodePos(k, i);
          const d = Math.hypot(n.x - s.pointer.x, n.y - s.pointer.y);
          if (d < bd) { bd = d; s.hot = { k, i }; }
        }));
      }
    };

    // ---- drawing
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';

      // route highlight (computed first so it sits under everything)
      const route = new Set();
      if (s.hot) {
        let k = s.hot.k, i = s.hot.i;
        while (k >= 0) { route.add(`${k}:${i}`); const j = nearestInner(k, i); if (j == null) break; k -= 1; i = j; }
      }

      // rings
      rings.forEach((ring, k) => {
        ctx.save();
        ctx.strokeStyle = theme.line; ctx.globalAlpha = 0.5; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(cx, cy, ring.r * R, 0, Math.PI * 2); ctx.stroke();
        // ticks
        ctx.globalAlpha = 0.35; ctx.setLineDash([1, 10]); ctx.lineDashOffset = -ring.rot * ring.r * R;
        ctx.beginPath(); ctx.arc(cx, cy, ring.r * R + 5, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();

      });

      // edges: every node to its inner neighbour
      ctx.save();
      rings.forEach((ring, k) => ring.angles.forEach((_, i) => {
        const from = nodePos(k, i);
        const to = k === 0 ? { x: cx, y: cy } : nodePos(k - 1, nearestInner(k, i));
        const on = route.has(`${k}:${i}`);
        const bow = bowPoint(from, to, cx, cy);
        ctx.strokeStyle = on ? theme.accent : theme.line;
        ctx.globalAlpha = on ? 0.9 : 0.22; ctx.lineWidth = on ? 1.4 : 0.8;
        ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.quadraticCurveTo(bow.x, bow.y, to.x, to.y); ctx.stroke();
      }));
      ctx.restore();

      // static: soft rust smudges with drifting filaments
      for (const c of STATIC) {
        const sp = staticPos(c);
        ctx.save();
        const g = ctx.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, sp.rad * 1.8);
        g.addColorStop(0, theme.staticCore); g.addColorStop(0.5, theme.staticMid); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(sp.x, sp.y, sp.rad * 1.8, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = theme.rust; ctx.globalAlpha = 0.28; ctx.lineWidth = 0.7;
        for (let f = 0; f < 5; f++) {
          const a = s.t * 0.5 + f * 1.26;
          ctx.beginPath(); ctx.moveTo(sp.x + Math.cos(a) * sp.rad * 0.2, sp.y + Math.sin(a) * sp.rad * 0.2);
          for (let j = 1; j <= 4; j++) {
            const rr = sp.rad * (0.2 + j * 0.3);
            ctx.lineTo(sp.x + Math.cos(a + j * 0.35 + Math.sin(s.t * 2 + j) * 0.1) * rr,
                       sp.y + Math.sin(a + j * 0.35 + Math.cos(s.t * 2 + j) * 0.1) * rr);
          }
          ctx.stroke();
        }
        ctx.restore();
      }

      // nodes
      rings.forEach((ring, k) => ring.angles.forEach((_, i) => {
        const n = nodePos(k, i);
        const on = route.has(`${k}:${i}`);
        const isHot = s.hot && s.hot.k === k && s.hot.i === i;
        ctx.save();
        ctx.fillStyle = theme.ground; ctx.beginPath(); ctx.arc(n.x, n.y, k === 3 ? 4.5 : 3.6, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = on ? theme.accent : theme.text; ctx.globalAlpha = on ? 1 : 0.55; ctx.lineWidth = isHot ? 2 : 1.2;
        ctx.beginPath(); ctx.arc(n.x, n.y, k === 3 ? 4.5 : 3.6, 0, Math.PI * 2); ctx.stroke();
        if (k === 3) { ctx.fillStyle = theme.gold; ctx.globalAlpha = 0.9; ctx.beginPath(); ctx.arc(n.x, n.y, 1.8, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      }));

      // ring labels, drawn above the nodes so a passing point never sits on a word
      rings.forEach((ring) => {
        ctx.save();
        ctx.font = `600 10px ${theme.font}`; ctx.letterSpacing = '0.16em'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const lbl = ring.label.toUpperCase(); const tw = ctx.measureText(lbl).width + 18;
        const ly = cy - ring.r * R;
        ctx.fillStyle = theme.ground; ctx.globalAlpha = 0.95;
        ctx.beginPath(); ctx.roundRect(cx - tw / 2, ly - 8, tw, 16, 8); ctx.fill();
        ctx.fillStyle = theme.text; ctx.globalAlpha = 0.6; ctx.fillText(lbl, cx, ly + 0.5);
        ctx.restore();
      });

      // arrival pulses at the centre
      for (const p of s.pulses) {
        ctx.save(); ctx.globalAlpha = p.life * 0.35; ctx.strokeStyle = theme.sage; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(cx, cy, 14 + (1 - p.life) * 30, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
      }

      // shield
      ctx.save();
      const sr = SHIELD_R * R * (1 + s.shieldPulse * 0.05);
      const g = ctx.createRadialGradient(cx, cy, sr * 0.5, cx, cy, sr * 1.5);
      g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(0.7, theme.sageSoft); g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, sr * 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = theme.sage; ctx.lineWidth = 1.6 + s.shieldPulse * 1.5; ctx.globalAlpha = 0.9;
      ctx.shadowColor = theme.sage; ctx.shadowBlur = 10 + s.shieldPulse * 16;
      ctx.beginPath(); ctx.arc(cx, cy, sr, 0, Math.PI * 2); ctx.stroke();
      ctx.shadowBlur = 0; ctx.setLineDash([1, 5]); ctx.lineWidth = 1; ctx.globalAlpha = 0.5;
      ctx.beginPath(); ctx.arc(cx, cy, sr + 6, s.t * 0.3, s.t * 0.3 + Math.PI * 2); ctx.stroke();
      ctx.restore();
      ctx.save();
      ctx.font = `600 10px ${theme.font}`; ctx.letterSpacing = '0.16em'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = theme.sage; ctx.globalAlpha = 0.9; ctx.fillText('SHIELD', cx, cy + sr + 16);
      ctx.restore();

      // packets
      for (const p of s.packets) {
        const col = p.taint > 0.05 ? mix(theme.packet, theme.rust, p.taint) : p.clean > 0 ? mix(theme.packet, theme.gold, p.clean) : theme.packet;
        ctx.save();
        for (let i = 1; i < p.trail.length; i++) {
          ctx.globalAlpha = (i / p.trail.length) * 0.35; ctx.strokeStyle = col; ctx.lineWidth = 1.6 * (i / p.trail.length);
          ctx.beginPath(); ctx.moveTo(p.trail[i - 1].x, p.trail[i - 1].y); ctx.lineTo(p.trail[i].x, p.trail[i].y); ctx.stroke();
        }
        const jx = p.taint * (Math.random() - 0.5) * 3, jy = p.taint * (Math.random() - 0.5) * 3;
        ctx.globalAlpha = 1; ctx.fillStyle = col;
        ctx.shadowColor = col; ctx.shadowBlur = 6 + p.clean * 10;
        ctx.beginPath(); ctx.arc(p.x + jx, p.y + jy, 2.2 + p.clean, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      }

      // sparks
      for (const k of s.sparks) {
        ctx.save(); ctx.globalAlpha = Math.max(0, k.life); ctx.fillStyle = k.kind === 'static' ? theme.rust : theme.gold;
        ctx.beginPath(); ctx.arc(k.x, k.y, k.kind === 'static' ? 1 : 1.4, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      }

      // you
      ctx.save();
      ctx.fillStyle = theme.text; ctx.beginPath(); ctx.roundRect(cx - 7, cy - 12, 14, 24, 4); ctx.fill();
      ctx.fillStyle = theme.ground; ctx.beginPath(); ctx.roundRect(cx - 5.5, cy - 10, 11, 18, 2.5); ctx.fill();
      ctx.fillStyle = theme.sage; ctx.globalAlpha = 0.85; ctx.beginPath(); ctx.roundRect(cx - 5.5, cy - 10, 11, 18, 2.5); ctx.fill();
      ctx.restore();
    };

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      if (W > 0) { step(dt); draw(); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); obs.disconnect();
      canvas.removeEventListener('pointermove', onMove); canvas.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return <canvas ref={ref} className={className} style={{ width: '100%', height, display: 'block' }} />;
}

// ---------------------------------------------------------------- helpers
function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  const v = (n) => cs.getPropertyValue(n).trim();
  const dark = document.documentElement.classList.contains('dark');
  return {
    text: v('--text'), ground: v('--ground'), sage: v('--sage'), gold: v('--gold'), rust: v('--rust'), accent: v('--clay'),
    line: dark ? '#f1ebe2' : '#3e3022',
    packet: dark ? '#f1ebe2' : '#5a4a3a',
    sageSoft: dark ? 'rgba(147,178,149,0.22)' : 'rgba(111,143,114,0.22)',
    staticCore: dark ? 'rgba(120,70,60,0.30)' : 'rgba(110,70,58,0.16)',
    staticMid: dark ? 'rgba(70,45,40,0.22)' : 'rgba(90,60,50,0.07)',
    font: v('--font-sans') || 'sans-serif',
  };
}
const hash = (n) => { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); };
const angDiff = (a, b) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
const easeInOut = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const quad = (a, b, c, t) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * b + t * t * c;
/** Control point that bows a leg gently sideways, so the web reads as woven rather than spoked. */
function bowPoint(from, to, cx, cy) {
  const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2;
  const dx = to.x - from.x, dy = to.y - from.y, len = Math.hypot(dx, dy) || 1;
  const side = ((from.x - cx) * (to.y - cy) - (from.y - cy) * (to.x - cx)) > 0 ? 1 : -1;
  return { x: mx + (-dy / len) * len * 0.18 * side, y: my + (dx / len) * len * 0.18 * side };
}
function spark(x, y, kind) {
  const a = Math.random() * Math.PI * 2, sp = 14 + Math.random() * 40;
  return { x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0.5 + Math.random() * 0.4, kind };
}
const hexToRgb = (h) => { const m = h.replace('#', ''); const n = parseInt(m.length === 3 ? m.split('').map((c) => c + c).join('') : m, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
function mix(a, b, t) {
  if (!a.startsWith('#') || !b.startsWith('#')) return t > 0.5 ? b : a;
  const A = hexToRgb(a), B = hexToRgb(b);
  return `rgb(${A.map((v, i) => Math.round(v + (B[i] - v) * t)).join(',')})`;
}
